/* eslint-disable indent */
import React, { useState } from 'react'
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap'
import axios from 'axios'

/**
 * Poimii kaikki 9-numeroiset pelaaja-ID:t tekstistä.
 * Toimii riippumatta muotoilusta: (ID), [ID], ID, ID ID ID jne.
 * @param {string} text - Syöteteksti
 * @returns {string[]} - Uniikki lista 9-numeroisista ID:istä
 */
export const extractPlayerIds = text => {
  if (!text) return []
  // Etsitään kaikki 9-numeroiset sarjat
  const matches = text.match(/\d{9}/g)
  if (!matches) return []
  // Palautetaan uniikkilista
  return [...new Set(matches)]
}

const AddPlayersByIdModal = ({
  show,
  onHide,
  onAddPlayers,
  existingPlayerIds,
}) => {
  const [inputText, setInputText] = useState('')
  const [duplicates, setDuplicates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Tietokannasta löydetyt pelaajat
  const [dbFoundPlayers, setDbFoundPlayers] = useState([])
  const [dbNotFoundIds, setDbNotFoundIds] = useState([])

  // Hattrick-haku state
  const [searchPhase, setSearchPhase] = useState('input') // 'input', 'hattrick-results'
  const [hattrickFoundPlayers, setHattrickFoundPlayers] = useState([])
  const [hattrickNotFoundIds, setHattrickNotFoundIds] = useState([])

  // Parsii ID:t syötetekstistä ja hakee tietokannasta
  const handleParseIds = async () => {
    setError('')
    setSuccess('')
    const ids = extractPlayerIds(inputText)
    if (ids.length === 0) {
      setError('Tekstistä ei löytynyt yhtään 9-numeroista pelaaja-ID:tä.')
      setDbFoundPlayers([])
      setDbNotFoundIds([])
      setDuplicates([])
      return
    }

    // Tarkista duplikaatit (jo listalla olevat)
    const existingSet = new Set(existingPlayerIds || [])
    const newIds = ids.filter(id => !existingSet.has(id))
    const dupes = ids.filter(id => existingSet.has(id))

    setDuplicates(dupes)

    if (newIds.length === 0 && dupes.length > 0) {
      setError('Kaikki löydetyt pelaajat ovat jo listalla.')
      setDbFoundPlayers([])
      setDbNotFoundIds([])
      return
    }

    // Hae tietokannasta
    setIsLoading(true)
    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      const response = await axios.post(
        '/api/players/lookup-by-ids',
        {
          playerIds: newIds,
        },
        { headers: { 'x-oauth-token': token } }
      )

      setDbFoundPlayers(response.data.found)
      setDbNotFoundIds(response.data.notFound)
    } catch (err) {
      console.error('Error looking up players:', err)
      // Jos lookup epäonnistuu, näytä kaikki "ei löytyneinä"
      setDbFoundPlayers([])
      setDbNotFoundIds(newIds)
    } finally {
      setIsLoading(false)
    }
  }

  // Hae pelaajat Hattrickista (vain ne jotka eivät löytyneet tietokannasta)
  const handleSearchHattrick = async () => {
    if (dbNotFoundIds.length === 0) {
      setError('Ei haettavia pelaajia.')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      const response = await axios.post(
        '/api/players/search-hattrick',
        { playerIds: dbNotFoundIds },
        { headers: { 'x-oauth-token': token } }
      )

      setHattrickFoundPlayers(response.data.found)
      setHattrickNotFoundIds(response.data.notFound)
      setSearchPhase('hattrick-results')
    } catch (err) {
      setError(
        'Hattrickista haku epäonnistui: ' +
          (err.response?.data?.error || err.message)
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Poista pelaaja tietokantalistasta
  const handleRemoveDbPlayer = playerId => {
    setDbFoundPlayers(prev => prev.filter(p => p.playerId !== playerId))
  }

  // Poista pelaaja Hattrick-listasta
  const handleRemoveHattrickPlayer = playerId => {
    setHattrickFoundPlayers(prev => prev.filter(p => p.playerId !== playerId))
  }

  // Lisää pelaajat listalle
  const handleAddPlayers = async () => {
    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      if (searchPhase === 'hattrick-results') {
        // Lisää sekä tietokannasta löydetyt että Hattrickista haetut
        const allPlayerIds = [
          ...dbFoundPlayers.map(p => p.playerId.toString()),
          ...hattrickFoundPlayers.map(p => p.playerId.toString()),
        ]

        if (allPlayerIds.length === 0) {
          setError('Ei lisättäviä pelaajia.')
          setIsLoading(false)
          return
        }

        // 1. Tallenna Hattrickista haetut pelaajat tietokantaan
        if (hattrickFoundPlayers.length > 0) {
          await axios.post(
            '/api/players/add-from-hattrick',
            { players: hattrickFoundPlayers },
            { headers: { 'x-oauth-token': token } }
          )
        }

        // 2. Lisää kaikki ehdokaslistalle
        await onAddPlayers(allPlayerIds)

        setSuccess(`${allPlayerIds.length} pelaajaa lisätty onnistuneesti!`)
      } else {
        // Vain tietokannasta löydetyt pelaajat
        if (dbFoundPlayers.length === 0) {
          setError('Ei lisättäviä pelaajia.')
          setIsLoading(false)
          return
        }

        await onAddPlayers(dbFoundPlayers.map(p => p.playerId.toString()))
        setSuccess(`${dbFoundPlayers.length} pelaajaa lisätty onnistuneesti!`)
      }

      // Suljetaan modal pienen viiveen jälkeen
      setTimeout(() => {
        handleClose()
      }, 1500)
    } catch (err) {
      setError(err.message || 'Pelaajien lisääminen epäonnistui.')
    } finally {
      setIsLoading(false)
    }
  }

  // Nollaa tila kun modal suljetaan
  const handleClose = () => {
    setInputText('')
    setDbFoundPlayers([])
    setDbNotFoundIds([])
    setDuplicates([])
    setError('')
    setSuccess('')
    setSearchPhase('input')
    setHattrickFoundPlayers([])
    setHattrickNotFoundIds([])
    onHide()
  }

  // Palaa takaisin input-vaiheeseen
  const handleBack = () => {
    setSearchPhase('input')
    setHattrickFoundPlayers([])
    setHattrickNotFoundIds([])
    setError('')
  }

  // Laske lisättävien pelaajien määrä
  const getTotalPlayersToAdd = () => {
    if (searchPhase === 'hattrick-results') {
      return dbFoundPlayers.length + hattrickFoundPlayers.length
    }
    return dbFoundPlayers.length
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ backgroundColor: '#23272A', color: '#FFFFFF' }}
      >
        <Modal.Title>Lisää pelaajia ID:n perusteella</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#2C2F33', color: '#FFFFFF' }}>
        {searchPhase === 'input' ? (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Syötä pelaajien ID:t (9 numeroinen):</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Esim: Joni-Matti Klemelä (458247223)(458581243)455452832455108590&#10;Tai: 458247223, 458581243, 455452832&#10;Tai pelkät ID:t: 458247223 458581243"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                style={{
                  backgroundColor: '#1E2124',
                  color: '#FFFFFF',
                  borderColor: '#444',
                }}
              />
              <Form.Text style={{ color: '#888' }}>
                Järjestelmä tunnistaa automaattisesti kaikki 9-numeroiset ID:t
                tekstistä riippumatta muotoilusta.
              </Form.Text>
            </Form.Group>

            <Button
              variant="secondary"
              onClick={handleParseIds}
              className="mb-3"
              disabled={!inputText.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Haetaan...
                </>
              ) : (
                'Tunnista ID:t'
              )}
            </Button>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {/* Tietokannasta löydetyt pelaajat */}
            {dbFoundPlayers.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#2ecc71' }}>
                  ✓ Löytyi tietokannasta ({dbFoundPlayers.length} kpl):
                </h6>
                <div
                  style={{
                    backgroundColor: '#1E2124',
                    borderRadius: '5px',
                    padding: '10px',
                  }}
                >
                  {dbFoundPlayers.map(player => (
                    <div
                      key={player.playerId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 10px',
                        marginBottom: '5px',
                        backgroundColor: '#2C2F33',
                        borderRadius: '3px',
                        borderLeft: '3px solid #2ecc71',
                      }}
                    >
                      <span>
                        {player.firstName} {player.lastName}{' '}
                        <span style={{ color: '#888', fontSize: '0.85em' }}>
                          ({player.playerId})
                        </span>
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleRemoveDbPlayer(player.playerId)}
                        style={{ color: '#e74c3c', padding: '0 5px' }}
                        title="Poista listalta"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tietokannasta ei löytyneet */}
            {dbNotFoundIds.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#f39c12' }}>
                  ⚠ Ei löytynyt tietokannasta ({dbNotFoundIds.length} kpl):
                </h6>
                <div
                  style={{
                    backgroundColor: '#1E2124',
                    borderRadius: '5px',
                    padding: '10px',
                  }}
                >
                  {dbNotFoundIds.map(id => (
                    <div
                      key={id}
                      style={{
                        padding: '5px 10px',
                        marginBottom: '5px',
                        backgroundColor: '#2C2F33',
                        borderRadius: '3px',
                        borderLeft: '3px solid #f39c12',
                        color: '#f39c12',
                      }}
                    >
                      ID: {id}
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline-info"
                  size="sm"
                  className="mt-2"
                  onClick={handleSearchHattrick}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Haetaan...
                    </>
                  ) : (
                    `🔍 Hae Hattrickista (${dbNotFoundIds.length} kpl)`
                  )}
                </Button>
              </div>
            )}

            {duplicates.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#888' }}>
                  Jo listalla olevat ({duplicates.length} kpl):
                </h6>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {duplicates.map(id => (
                    <span
                      key={id}
                      style={{
                        backgroundColor: '#444',
                        padding: '2px 8px',
                        borderRadius: '3px',
                        fontSize: '0.85em',
                      }}
                    >
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Hattrick-results phase */}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {/* Tietokannasta löydetyt (pysyvät näkyvissä) */}
            {dbFoundPlayers.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#2ecc71' }}>
                  ✓ Tietokannasta ({dbFoundPlayers.length} kpl):
                </h6>
                <div
                  style={{
                    backgroundColor: '#1E2124',
                    borderRadius: '5px',
                    padding: '10px',
                  }}
                >
                  {dbFoundPlayers.map(player => (
                    <div
                      key={player.playerId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 10px',
                        marginBottom: '5px',
                        backgroundColor: '#2C2F33',
                        borderRadius: '3px',
                        borderLeft: '3px solid #2ecc71',
                      }}
                    >
                      <span>
                        {player.firstName} {player.lastName}{' '}
                        <span style={{ color: '#888', fontSize: '0.85em' }}>
                          ({player.playerId})
                        </span>
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleRemoveDbPlayer(player.playerId)}
                        style={{ color: '#e74c3c', padding: '0 5px' }}
                        title="Poista listalta"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hattrickista löydetyt */}
            {hattrickFoundPlayers.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#3498db' }}>
                  ✓ Löytyi Hattrickista ({hattrickFoundPlayers.length} kpl):
                </h6>
                <div
                  style={{
                    backgroundColor: '#1E2124',
                    borderRadius: '5px',
                    padding: '10px',
                  }}
                >
                  {hattrickFoundPlayers.map(player => (
                    <div
                      key={player.playerId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 10px',
                        marginBottom: '5px',
                        backgroundColor: '#2C2F33',
                        borderRadius: '3px',
                        borderLeft: '3px solid #3498db',
                      }}
                    >
                      <span>
                        {player.firstName} {player.lastName}{' '}
                        <span style={{ color: '#888', fontSize: '0.85em' }}>
                          ({player.playerId})
                        </span>
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() =>
                          handleRemoveHattrickPlayer(player.playerId)
                        }
                        style={{ color: '#e74c3c', padding: '0 5px' }}
                        title="Poista listalta"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hattrickistakaan ei löytyneet */}
            {hattrickNotFoundIds.length > 0 && (
              <div className="mb-3">
                <h6 style={{ color: '#e74c3c' }}>
                  ✗ Ei löytynyt ({hattrickNotFoundIds.length} kpl):
                </h6>
                <div
                  style={{
                    backgroundColor: '#1E2124',
                    borderRadius: '5px',
                    padding: '10px',
                  }}
                >
                  {hattrickNotFoundIds.map(id => (
                    <div
                      key={id}
                      style={{
                        padding: '5px 10px',
                        marginBottom: '5px',
                        backgroundColor: '#2C2F33',
                        borderRadius: '3px',
                        borderLeft: '3px solid #e74c3c',
                        color: '#e74c3c',
                      }}
                    >
                      ID: {id} (ei löytynyt)
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dbFoundPlayers.length === 0 &&
              hattrickFoundPlayers.length === 0 &&
              hattrickNotFoundIds.length === 0 && (
                <Alert variant="info">Ei tuloksia</Alert>
              )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: '#23272A' }}>
        {searchPhase === 'hattrick-results' && (
          <Button variant="outline-secondary" onClick={handleBack}>
            ← Takaisin
          </Button>
        )}
        <Button variant="secondary" onClick={handleClose}>
          Peruuta
        </Button>
        <Button
          variant="info"
          onClick={handleAddPlayers}
          disabled={getTotalPlayersToAdd() === 0 || isLoading}
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Lisätään...
            </>
          ) : (
            `Lisää ${getTotalPlayersToAdd()} pelaajaa`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddPlayersByIdModal
